/**
 * Sends `/assessment/addStudentsToAssessment` in sequential chunks of
 * `chunkSize` (default 20) instead of a single large POST.
 *
 * The single-shot path 500-row uploads sustained ~10-wide concurrent Prisma writes
 * inside admin-node long enough to SIGABRT the Prisma 4.x query engine subprocess on
 * ARM64. Chunking caps in-flight work per request and gives the user visible
 * progress; it also localises any partial failure to a single chunk so the user
 * knows exactly which rows didn't make it through.
 *
 * @param {Object}   params
 * @param {Object}   params.client          Axios instance (e.g. adminRequest), or null to use raw axios.
 * @param {string}   params.url             Endpoint path or full URL.
 * @param {Object}   params.basePayload     Payload WITHOUT `bulkUploadData` (assessmentInstituteMapId, entityType, etc.).
 * @param {Array}    params.bulkUploadData  Full row list to be chunked.
 * @param {number}  [params.chunkSize=20]   Rows per request.
 * @param {Function}[params.onProgress]     Called as ({ chunkIndex, totalChunks, addedSoFar, skippedSoFar, failedSoFar }) before each chunk and after the last.
 * @param {Object}  [params.axiosClient]    Raw axios module to use when `client` is null (used by call sites that hit `${ADMIN_API_URL}...` with shared `axios`).
 *
 * @returns {Promise<{addedCount, skippedCount, failedCount, chunkErrors, responses}>}
 *   Aggregated counts plus per-chunk errors. Best-effort: a failing chunk does not
 *   abort subsequent chunks (engine panics often clear within ~30s of pod restart).
 */
export async function bulkAddStudentsToAssessment({
  client,
  url,
  basePayload,
  bulkUploadData,
  chunkSize = 20,
  onProgress,
  axiosClient,
}) {
  const rows = Array.isArray(bulkUploadData) ? bulkUploadData : []
  const totalChunks = Math.max(1, Math.ceil(rows.length / chunkSize))
  const chunkErrors = []
  const responses = []
  let addedCount = 0
  let skippedCount = 0
  let failedCount = 0

  const fire = (payload) => {
    if (client) return client.post(url, payload)
    if (axiosClient) return axiosClient.post(url, payload)
    throw new Error('bulkAddStudentsToAssessment: client or axiosClient is required')
  }

  for (let i = 0; i < totalChunks; i += 1) {
    const slice = rows.slice(i * chunkSize, (i + 1) * chunkSize)
    if (typeof onProgress === 'function') {
      onProgress({
        chunkIndex: i,
        totalChunks,
        addedSoFar: addedCount,
        skippedSoFar: skippedCount,
        failedSoFar: failedCount,
        rowsInChunk: slice.length,
      })
    }
    try {
      const response = await fire({ ...basePayload, bulkUploadData: slice })
      responses.push(response)
      const data = response?.data?.data || {}
      addedCount += Number(data.addedCount || 0)
      skippedCount += Number(data.skippedCount || 0)
      failedCount += Number(data.failedAccountCount || 0)
    } catch (err) {
      // Chunk failed entirely: count every row in this slice as failed for
      // reporting purposes and continue with the next chunk.
      failedCount += slice.length
      chunkErrors.push({
        chunkIndex: i,
        rowsInChunk: slice.length,
        firstEmail: slice[0]?.primary_email || slice[0]?.email,
        lastEmail: slice[slice.length - 1]?.primary_email || slice[slice.length - 1]?.email,
        message: err?.response?.data?.message || err?.message || 'Chunk request failed',
        status: err?.response?.status,
      })
    }
  }

  if (typeof onProgress === 'function') {
    onProgress({
      chunkIndex: totalChunks,
      totalChunks,
      addedSoFar: addedCount,
      skippedSoFar: skippedCount,
      failedSoFar: failedCount,
      rowsInChunk: 0,
      done: true,
    })
  }

  return { addedCount, skippedCount, failedCount, chunkErrors, responses }
}

export default bulkAddStudentsToAssessment
